import React, { useState } from 'react';
import { Search, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ConversasList = ({
  conversas,
  conversaAtual,
  onSelecionarConversa,
  usuariosOnline,
  className = '',
}) => {
  const [filtro, setFiltro] = useState('');

  const conversasFiltradas = conversas.filter((conversa) =>
    conversa.outroUsuario.nome.toLowerCase().includes(filtro.toLowerCase())
  );

  const formatarData = (data) => {
    const date = new Date(data);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(ontem.getDate() - 1);

    if (date.toDateString() === hoje.toDateString()) {
      return format(date, 'HH:mm', { locale: ptBR });
    } else if (date.toDateString() === ontem.toDateString()) {
      return 'Ontem';
    } else {
      return format(date, 'dd/MM/yyyy', { locale: ptBR });
    }
  };

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-dark-card ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-3">
          💬 Conversas
        </h2>

        {/* Busca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar conversa..."
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="w-full pl-9 pr-9 py-2 bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg text-sm text-gray-900 dark:text-dark-text placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          {filtro && (
            <button
              onClick={() => setFiltro('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Lista de Conversas */}
      <div className="flex-1 overflow-y-auto">
        {conversasFiltradas.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 dark:text-dark-muted text-sm">
            {filtro ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa ainda'}
          </div>
        ) : (
          conversasFiltradas.map((conversa, idx) => {
            const online = usuariosOnline.has(conversa.outroUsuario.id);
            const ativo = conversaAtual?.conversaId === conversa.conversaId;
            const key = conversa.conversaId ?? conversa.outroUsuario?.id ?? `conversa-${idx}`;

            return (
              <div
                key={key}
                onClick={() => onSelecionarConversa(conversa)}
                className={`p-4 border-b border-gray-200 dark:border-dark-border cursor-pointer transition-colors ${
                  ativo
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-l-primary-500'
                    : 'hover:bg-gray-50 dark:hover:bg-dark-bg'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {conversa.outroUsuario?.fotoPerfil ? (
                      <img
                        src={conversa.outroUsuario.fotoPerfil}
                        alt={conversa.outroUsuario.nome}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = '/default-profile.webp';
                        }}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold">
                        {conversa.outroUsuario.nome.charAt(0).toUpperCase()}
                      </div>
                    )}
                    {/* Indicador Online */}
                    {online && (
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-dark-card rounded-full"></div>
                    )}
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-semibold text-gray-900 dark:text-dark-text text-sm truncate">
                        {conversa.outroUsuario.nome}
                      </h3>
                      {conversa.ultimaMensagemData && (
                        <span className="text-xs text-gray-500 dark:text-dark-muted ml-2 flex-shrink-0">
                          {formatarData(conversa.ultimaMensagemData)}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600 dark:text-dark-muted truncate">
                        {conversa.ultimaMensagem || 'Nenhuma mensagem ainda'}
                      </p>
                      {conversa.mensagensNaoLidas > 0 && (
                        <span className="ml-2 flex-shrink-0 bg-red-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                          {conversa.mensagensNaoLidas}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversasList;
